import useMediaQ from "@root/hooks/useMediaQ";
import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";

const CustomVideoPlayer: React.FC<{
  url: string;
  onPlay?: () => void;
  onPause?: (progress?: number) => void;
  onEnded?: () => void;
  onProgress?: (progress: number) => void;
  initialProgress?: number | null; // Percentage (0-100) to resume from
}> = ({ url, onPlay, onPause, onEnded, onProgress, initialProgress = null }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<any | null | undefined>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlayerInitialized, setIsPlayerInitialized] = useState(false);
  const { sm } = useMediaQ();
  const isMobile = !sm;

  console.log("isMobile in CustomVideoPlayer", isMobile, isFullscreen);

  // Initialize player only once
  useEffect(() => {
    if (videoRef?.current && !isPlayerInitialized) {
      const player = videojs(videoRef?.current, {
        controls: true,
        preload: "auto",
        width: 640,
        height: 360,
      });

      playerRef.current = player;
      setIsPlayerInitialized(true);

      // Prevent forward seeking beyond watched time without restarting
      // If resuming from saved progress, initialize maxAllowedTime to that position
      let maxAllowedTime = 0;
      let isAdjustingSeek = false;
      let hasInitialSeeked = false;

      const clampForwardSeek = () => {
        const desiredTime = player?.currentTime?.() ?? 0;
        if (desiredTime > maxAllowedTime + 0.5) {
          isAdjustingSeek = true;
          player?.currentTime?.(maxAllowedTime);
        }
      };

      player.on("timeupdate", () => {
        if (!player?.seeking?.() && !isAdjustingSeek) {
          const current = player?.currentTime?.() ?? 0;
          if (!Number.isNaN(current)) {
            maxAllowedTime = Math.max(maxAllowedTime, current);
          }
          // Call onProgress callback with percentage
          if (onProgress) {
            const duration = player?.duration?.() ?? 0;
            if (duration > 0 && current > 0) {
              const progressPercentage = (current / duration) * 100;
              onProgress(progressPercentage);
            }
          }
        }
      });

      player.on("seeking", () => {
        if (isAdjustingSeek) {
          isAdjustingSeek = false;
        } else {
          clampForwardSeek();
        }
        // if (!isAdjustingSeek) {
        //   clampForwardSeek();
        // }
      });

      player.on("seeked", () => {
        if (isAdjustingSeek) {
          isAdjustingSeek = false;
        } else {
          clampForwardSeek();
        }
      });

      // Clamp progress bar dragging and clicks beyond allowed time
      const progressEl: any = (
        player as any
      )?.controlBar?.progressControl?.el?.();
      let isDraggingOnBar = false;

      const getDesiredTimeFromEvent = (evt: any) => {
        if (!progressEl) return undefined;
        const rect = progressEl?.getBoundingClientRect?.();
        if (!rect) return undefined;
        const clientX = evt?.touches?.[0]?.clientX ?? evt?.clientX;
        if (typeof clientX !== "number") return undefined;
        const fraction = Math.max(
          0,
          Math.min(1, (clientX - rect.left) / rect.width)
        );
        const duration = player?.duration?.() ?? 0;
        if (!duration || Number.isNaN(duration)) return 0;
        return fraction * duration;
      };

      const onBarDown = (evt: any) => {
        isDraggingOnBar = true;
        const desired = getDesiredTimeFromEvent(evt);
        if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
          evt?.preventDefault?.();
          evt?.stopPropagation?.();
          player?.currentTime?.(maxAllowedTime);
          return false;
        }
      };

      const onBarMove = (evt: any) => {
        if (!isDraggingOnBar) return;
        const desired = getDesiredTimeFromEvent(evt);
        if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
          evt?.preventDefault?.();
          evt?.stopPropagation?.();
          player?.currentTime?.(maxAllowedTime);
          return false;
        }
      };

      const onBarUp = (evt: any) => {
        if (isDraggingOnBar) {
          const desired = getDesiredTimeFromEvent(evt);
          if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
            evt?.preventDefault?.();
            evt?.stopPropagation?.();
            player?.currentTime?.(maxAllowedTime);
          }
        }
        isDraggingOnBar = false;
      };

      const onBarClick = (evt: any) => {
        const desired = getDesiredTimeFromEvent(evt);
        if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
          evt?.preventDefault?.();
          evt?.stopPropagation?.();
          player?.currentTime?.(maxAllowedTime);
          return false;
        }
      };

      // Override the progress control's seeking behavior
      const progressControl = (player as any)?.controlBar?.progressControl;
      let originalHandleMouseDown: any = null;
      let originalHandleMouseMove: any = null;
      let originalHandleMouseUp: any = null;

      if (progressControl) {
        originalHandleMouseDown = progressControl?.handleMouseDown;
        originalHandleMouseMove = progressControl?.handleMouseMove;
        originalHandleMouseUp = progressControl?.handleMouseUp;

        progressControl.handleMouseDown = function (evt: any) {
          const desired = getDesiredTimeFromEvent(evt);
          if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
            evt?.preventDefault?.();
            evt?.stopPropagation?.();
            evt?.stopImmediatePropagation?.();
            player?.currentTime?.(maxAllowedTime);
            return false;
          }
          return originalHandleMouseDown?.call(this, evt);
        };

        progressControl.handleMouseMove = function (evt: any) {
          const desired = getDesiredTimeFromEvent(evt);
          if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
            evt?.preventDefault?.();
            evt?.stopPropagation?.();
            evt?.stopImmediatePropagation?.();
            player?.currentTime?.(maxAllowedTime);
            return false;
          }
          return originalHandleMouseMove?.call(this, evt);
        };

        progressControl.handleMouseUp = function (evt: any) {
          const desired = getDesiredTimeFromEvent(evt);
          if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
            evt?.preventDefault?.();
            evt?.stopPropagation?.();
            evt?.stopImmediatePropagation?.();
            player?.currentTime?.(maxAllowedTime);
            return false;
          }
          isDraggingOnBar = false;
          return originalHandleMouseUp?.call(this, evt);
        };

        // Also override handleClick for direct clicks
        const originalHandleClick = progressControl?.handleClick;
        if (originalHandleClick) {
          progressControl.handleClick = function (evt: any) {
            const desired = getDesiredTimeFromEvent(evt);
            if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
              evt?.preventDefault?.();
              evt?.stopPropagation?.();
              evt?.stopImmediatePropagation?.();
              player?.currentTime?.(maxAllowedTime);
              return false;
            }
            return originalHandleClick?.call(this, evt);
          };
        }
      }

      // More aggressive approach - intercept all events on progress bar and its children
      const preventForwardSeek = (evt: any) => {
        const desired = getDesiredTimeFromEvent(evt);
        if (typeof desired === "number" && desired > maxAllowedTime + 0.5) {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          player.currentTime(maxAllowedTime);
          return false;
        }
      };

      if (progressEl && progressEl.addEventListener) {
        // Add capture phase listeners to intercept before Video.js handles them
        progressEl.addEventListener("mousedown", preventForwardSeek, {
          passive: false,
          capture: true,
        });
        progressEl.addEventListener("touchstart", preventForwardSeek, {
          passive: false,
          capture: true,
        });
        progressEl.addEventListener("click", preventForwardSeek, {
          passive: false,
          capture: true,
        });

        // Also add to all child elements
        const progressChildren = progressEl.querySelectorAll("*");
        progressChildren.forEach((child: any) => {
          child.addEventListener("mousedown", preventForwardSeek, {
            passive: false,
            capture: true,
          });
          child.addEventListener("touchstart", preventForwardSeek, {
            passive: false,
            capture: true,
          });
          child.addEventListener("click", preventForwardSeek, {
            passive: false,
            capture: true,
          });
        });

        // Keep the original listeners as backup
        progressEl.addEventListener("mousedown", onBarDown, { passive: false });
        progressEl.addEventListener("touchstart", onBarDown, {
          passive: false,
        });
        window.addEventListener("mousemove", onBarMove, { passive: false });
        window.addEventListener("touchmove", onBarMove, { passive: false });
        window.addEventListener("mouseup", onBarUp, { passive: false });
        window.addEventListener("touchend", onBarUp, { passive: false });
        progressEl.addEventListener("click", onBarClick, { passive: false });
      }

      // Listen for fullscreen changes
      player.on("fullscreenchange", () => {
        const fullscreenState = player?.isFullscreen();
        setIsFullscreen(fullscreenState ?? false);
        console.log("Fullscreen state changed:", fullscreenState);
      });

      // Bind event handlers
      if (onPlay) {
        player.on("play", () => {
          onPlay();
        });
      }

      if (onPause) {
        player.on("pause", () => {
          // Calculate progress percentage based on current time and duration
          const currentTime = player?.currentTime?.() || 0;
          const duration = player?.duration?.() || 0;
          const progressPercentage =
            duration > 0 ? (currentTime / duration) * 100 : 0;
          onPause(progressPercentage);
        });
      }

      if (onEnded) {
        player.on("ended", () => {
          onEnded();
        });
      }

      // Seek to initial progress when video metadata is loaded (duration available)
      if (initialProgress !== null && initialProgress !== undefined && initialProgress > 0) {
        const seekToInitialProgress = () => {
          if (hasInitialSeeked) return;
          const duration = player?.duration?.() ?? 0;
          if (duration > 0) {
            const seekTime = (initialProgress / 100) * duration;
            if (seekTime >= 0.5 && seekTime < duration) {
              hasInitialSeeked = true;
              // Update maxAllowedTime BEFORE seeking so anti-forward-seek doesn't block it
              maxAllowedTime = Math.max(maxAllowedTime, seekTime);
              player.currentTime(seekTime);
            }
          }
        };

        // Try on ready first (duration may already be available for cached videos)
        player.ready(() => {
          seekToInitialProgress();
        });

        // Also listen for loadedmetadata - this is when duration becomes available for MP4 files
        player.on("loadedmetadata", () => {
          seekToInitialProgress();
        });
      }

      // Cleanup progress bar listeners via effect cleanup
      const cleanup = () => {
        if (progressEl && progressEl.removeEventListener) {
          // Remove capture phase listeners
          progressEl.removeEventListener(
            "mousedown",
            preventForwardSeek as any,
            true
          );
          progressEl.removeEventListener(
            "touchstart",
            preventForwardSeek as any,
            true
          );
          progressEl.removeEventListener(
            "click",
            preventForwardSeek as any,
            true
          );

          // Remove listeners from child elements
          const progressChildren = progressEl.querySelectorAll("*");
          progressChildren.forEach((child: any) => {
            child.removeEventListener(
              "mousedown",
              preventForwardSeek as any,
              true
            );
            child.removeEventListener(
              "touchstart",
              preventForwardSeek as any,
              true
            );
            child.removeEventListener("click", preventForwardSeek as any, true);
          });

          // Remove original listeners
          progressEl.removeEventListener("mousedown", onBarDown as any);
          progressEl.removeEventListener("touchstart", onBarDown as any);
          window.removeEventListener("mousemove", onBarMove as any);
          window.removeEventListener("touchmove", onBarMove as any);
          window.removeEventListener("mouseup", onBarUp as any);
          window.removeEventListener("touchend", onBarUp as any);
          progressEl.removeEventListener("click", onBarClick as any);
        }

        // Restore original progress control methods
        if (progressControl) {
          if (originalHandleMouseDown) {
            progressControl.handleMouseDown = originalHandleMouseDown;
          }
          if (originalHandleMouseMove) {
            progressControl.handleMouseMove = originalHandleMouseMove;
          }
          if (originalHandleMouseUp) {
            progressControl.handleMouseUp = originalHandleMouseUp;
          }
          // Note: handleClick restoration is handled within the if block above
        }
      };

      return cleanup;
    }
  }, [isPlayerInitialized, onPlay, onPause, onEnded, onProgress]);

  // Handle initialProgress changes after player is initialized
  useEffect(() => {
    if (playerRef.current && isPlayerInitialized && initialProgress !== null && initialProgress !== undefined && initialProgress > 0) {
      const player = playerRef.current;

      const trySeek = () => {
        const duration = player?.duration?.() ?? 0;
        if (duration > 0) {
          const seekTime = (initialProgress / 100) * duration;
          if (seekTime >= 0.5 && seekTime < duration) {
            player.currentTime(seekTime);
          }
        }
      };

      // Try immediately (duration may already be loaded)
      trySeek();

      // Also wait for loadedmetadata in case duration isn't available yet
      const onMetadata = () => trySeek();
      player.on("loadedmetadata", onMetadata);

      return () => {
        player.off("loadedmetadata", onMetadata);
      };
    }
  }, [initialProgress, isPlayerInitialized]);

  // Update player dimensions when isMobile changes
  useEffect(() => {
    if (playerRef.current && isPlayerInitialized) {
      const player = playerRef.current;
      if (isMobile) {
        player.width(640);
        player.height(360);
      } else {
        player.width(640);
        player.height(isFullscreen ? 360 : 800);
      }
    }
  }, [isMobile, isFullscreen, isPlayerInitialized]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (
        playerRef.current &&
        typeof playerRef.current.dispose === "function"
      ) {
        try {
          playerRef.current.dispose();
        } catch (error) {
          console.warn("Error disposing video player on unmount:", error);
        }
      }
      playerRef.current = null;
    };
  }, []);

  // Fullscreen helpers removed as they were unused

  const renderVideoPlayer = () => {
    return (
      <video
        ref={videoRef}
        id="vid1"
        className="video-js vjs-default-skin vjs-big-play-centered"
        style={{
          width: "100%",
          height: isMobile ? "" : isFullscreen ? "" : "800px",
        }}
      >
        <source src={url} type="video/mp4" />
      </video>
    );
  };

  return (
    <div>
      {/* Render the video player */}
      {renderVideoPlayer()}
    </div>
  );
};

export default CustomVideoPlayer;
